import React, { useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { ThemeProvider, createTheme } from '@mui/material';
import { RiMailSendFill } from 'react-icons/ri';
import emailjs from '@emailjs/browser';

const theme = createTheme({
  palette: {
    primary: { main: '#F61832' },
    secondary: { main: '#C13E39' },
  },
});

const Contact = () => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [sending, setSending] = useState(false);

  const validationSchema = Yup.object({
    fullName: Yup.string().required('Full name is required').min(2).max(50),
    phoneNumber: Yup.string()
      .matches(/^[0-9+\-\s]+$/, 'Must be digits, spaces, + or - only')
      .min(10, 'Phone number too short')
      .max(15, 'Phone number too long')
      .required('Phone number is required'),
    email: Yup.string().email('Invalid email address').required('Email is required'),
    message: Yup.string().required('Message is required').min(10, 'Message is too short'),
  });

  const formik = useFormik({
    initialValues: {
      fullName: '',
      phoneNumber: '',
      email: '',
      message: '',
    },
    validationSchema,
    onSubmit: (values, { resetForm }) => {
      setSending(true);

      // Debug log:
      console.log('Sending email with values:', values);

      emailjs
        .send(
          'service_ky7nd2c',      // Replace with your EmailJS service ID
          'template_d5lrelp',     // Replace with your EmailJS template ID
          {
            fullName: values.fullName,        // Match these variable names with your EmailJS template
            phone: values.phoneNumber,
            email: values.email,
            message: values.message,
          },
          '--UMRPO7ERS7B9cHl'        // Replace with your EmailJS public key
        )
        .then(
          (result) => {
            setIsSubmitted(true);
            resetForm();
          },
          (error) => {
            alert('Failed to send message. Please try again later.');
            console.error('EmailJS error:', error);
          }
        )
        .finally(() => setSending(false));
    },
  });

  if (isSubmitted) {
    return (
      <div className="pt-8 max-w-[1200px] mx-auto px-8">
        <Typography variant="h5" className="text-center mb-4">
          Thank You, {formik.values.fullName || 'Friend'}!
        </Typography>
        <Typography className="text-center">
          We've received your message. You'll receive a confirmation email shortly.
        </Typography>
        <Button
          variant="contained"
          color="primary"
          fullWidth
          className="mt-6"
          onClick={() => setIsSubmitted(false)}
        >
          Send Another Message
        </Button>
      </div>
    );
  }

  return (
    <div className="pt-8 max-w-[1200px] mx-auto px-8">
      <title>Contact Gotenberg Solution</title>
      <section className="lg:flex gap-10">
        <div className="lg:w-6/12">
          <h3 className="text-lg font-bold pb-6">We'd Love to Hear From You!</h3>
          <p>
            Have questions, suggestions, or just want to say hello? Fill out the form below,
            and we'll get back to you as soon as possible. Your feedback helps us improve,
            and we're always happy to connect.
          </p>
          <p className="py-4">
            Don't hesitate—reach out today! We're here to help.
          </p>
        </div>

        <div className="lg:w-6/12 rounded-lg shadow-md p-4">
          <form onSubmit={formik.handleSubmit}>
            <div className="space-y-4">
              <TextField
                fullWidth
                id="fullName"
                name="fullName"
                label="Full Name"
                variant="outlined"
                value={formik.values.fullName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.fullName && Boolean(formik.errors.fullName)}
                helperText={formik.touched.fullName && formik.errors.fullName}
                className="mb-4"
              />

              <TextField
                fullWidth
                id="phoneNumber"
                name="phoneNumber"
                label="Phone Number"
                variant="outlined"
                value={formik.values.phoneNumber}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.phoneNumber && Boolean(formik.errors.phoneNumber)}
                helperText={formik.touched.phoneNumber && formik.errors.phoneNumber}
                className="mb-4"
              />

              <TextField
                fullWidth
                id="email"
                name="email"
                label="Email"
                variant="outlined"
                value={formik.values.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.email && Boolean(formik.errors.email)}
                helperText={formik.touched.email && formik.errors.email}
                className="mb-4"
              />

              <TextField
                fullWidth
                id="message"
                name="message"
                label="Message"
                variant="outlined"
                multiline
                rows={4}
                value={formik.values.message}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.message && Boolean(formik.errors.message)}
                helperText={formik.touched.message && formik.errors.message}
                className="mb-4"
              />

              <ThemeProvider theme={theme}>
                <Button
                  color="primary"
                  variant="contained"
                  fullWidth
                  type="submit"
                  className="mt-2 py-2"
                  size="large"
                  endIcon={<RiMailSendFill />}
                  disabled={sending}
                >
                  {sending ? 'Sending...' : 'Send Message'}
                </Button>
              </ThemeProvider>
            </div>
          </form>
        </div>
      </section>

      <section className="mt-20">
        <div className="relative pb-[75%] h-0">
          <iframe
            src="https://www.google.com/maps/embed?pb=..."
            className="absolute top-0 left-0 w-full h-full rounded-lg border-white border-4 shadow-md"
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title="Google Maps Location"
          />
        </div>
      </section>
    </div>
  );
};

export default Contact;
